/* eslint-disable @nx/enforce-module-boundaries */
/* eslint-disable @typescript-eslint/no-inferrable-types */
import React, { useEffect, useState } from 'react';
import {
  Typography,
  IconButton,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Pagination,
  Stack,
  Box,
  Dialog,
} from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import BackIcon from '../../../../assets/icons/ArrowLeft.svg';
import { useTheme } from '@mui/material/styles';
import plus from '../../../../assets/icons/PlusWhiteStroke.svg';
import search from '../../../../assets/icons/search.svg';
import deleteIcon from '../../../../assets/icons/delete1.svg';
import eye from '../../../../assets/icons/Eye.svg';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import useAxios from 'packages/novous_web/src/api/useAxios';
import AlertToast from '../../../../common/components/Alert'; // Import the AlertToast component
import { AlertColor } from '@mui/material/Alert';
import AddGroupVehicles from './AddGroupVehicles'; // Import the AddGroupVehicles modal
import InputField from 'packages/novous_web/src/common/components/InputField';
import DOMPurify from 'dompurify';

// If vehicles can be either string or object, use a union type.
interface VehicleObject {
  regNo: string;
  name: string;
}
type Vehicle = string | VehicleObject;

const EditGroup: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { groupId } = useParams<{ groupId: string }>();
  // Access groupId from both params and state (for demonstration)
  const stateGroupId = location.state?.groupId;
  const fleetId = location.state?.fleetId;

  const effectiveGroupId = groupId || stateGroupId;
  const [groupName, setGroupName] = useState<string>('');
  const [groupDescription, setGroupDescription] = useState<string>('');
  const [depotCode, setDepotCode] = useState('');
  const [region, setRegion] = useState('');
  const [fleetManagerId, setFleetManagerId] = useState<string>(''); // New state for fleet manager ID
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>(''); // State for search term
  const [currentPage, setCurrentPage] = useState<number>(1); // New state for pagination
  const pageSize = 10;
  const theme = useTheme();

  // Toast state
  const [toastOpen, setToastOpen] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<AlertColor>('info');

  // State to control the AddGroupVehicles modal
  const [openAddVehiclesModal, setOpenAddVehiclesModal] = useState<boolean>(false);

  // Hook for GET group details
  const {
    data: groupData,
    loading: groupLoading,
    error: groupError,
    fetchData: fetchGroupData,
  } = useAxios<{ data: any }>(
    `${API_ENDPOINTS.directories.GROUPS_DETAIL}${fleetId}/${effectiveGroupId}`,
    'GET',
  );

  // Hook for POST (editing group)
  const { data: addVehicleData, error: fetchDataErrror, fetchData: fetchAddVehicleData } = useAxios<{ data: any[] }>(
    API_ENDPOINTS.directories.GROUP_EDIT,
    'POST',
  );

  useEffect(() => {
    if (effectiveGroupId) {
      fetchGroupData();
    }
  }, [effectiveGroupId]);

  useEffect(() => {
    if (groupData?.data) {
      const { groupName, groupDescription, vehicleList, fleetManagerId, depotCode, region } = groupData.data;
      // Set group details and fleet manager id
      setGroupName(groupName || '');
      setGroupDescription(groupDescription || '');
      setFleetManagerId(fleetManagerId || '');
      setDepotCode(depotCode || '');
      setRegion(region || '');
      if (Array.isArray(vehicleList)) {
        setVehicles(vehicleList);
      } else {
        console.warn('vehicleList is not an array:', vehicleList);
        setVehicles([]);
      }
    }
  }, [groupData]);

  // Filter vehicles based on the search term (case-insensitive)
  const filteredVehicles = vehicles.filter((vehicle) => {
    let vehicleStr = '';
    if (typeof vehicle === 'string') {
      vehicleStr = vehicle;
    } else {
      vehicleStr = `${vehicle.regNo || ''} ${vehicle.name || ''}`;
    }
    return vehicleStr.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Compute pagination values and slice filtered vehicles
  const totalPages = Math.ceil(filteredVehicles.length / pageSize);
  const paginatedVehicles = filteredVehicles.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Handler to update current page
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number): void => {
    setCurrentPage(page);
  };

  // Handler to delete a vehicle by index
  const handleDeleteVehicle = (index: number): void => {
    setVehicles((prevVehicles) => prevVehicles.filter((_, i) => i !== index));
  };

  // Handler to call the GROUP_EDIT endpoint with the current group details and vehicles.
  const handleSaveChanges = (): void => {
    const payload = {
      fleetManagerId,
      groupName,
      depotCode,
      region,
      groupDescription,
      vehicleList: vehicles,
    };

    if (groupDescription && !SAFE_TEXT.test(groupDescription)) {
      setToastMessage("depot Description cannot contain + - = @");
      setToastType("error");
      setToastOpen(true);
      return;
    }

    fetchAddVehicleData(payload)
      .then((response) => {
        setToastMessage('Depot Edited successfully!');
        setToastType('success');
        setToastOpen(true);
        setTimeout(() => {
          setToastOpen(false);
          navigate(-1);
        }, 2000);
      })
      .catch((err) => {
        console.error('Error updating depot:', err);
        setToastMessage('Failed to update depot.');
        setToastType('error');
        setToastOpen(true);
        setTimeout(() => {
          setToastOpen(false);
        }, 2000);
      });
  };

  useEffect(() => {
    if (fetchDataErrror) {
      setToastMessage(fetchDataErrror);
      setToastType('error');
      setToastOpen(true);
    }
  }, [fetchDataErrror])

  // Header Back Button handler
  const handleBackClick = (): void => {
    navigate(-1);
  };

  // Handler for opening the AddGroupVehicles modal
  const handleAddVehiclesModalOpen = (): void => {
    setOpenAddVehiclesModal(true);
  };

  // Handler for closing the AddGroupVehicles modal
  const handleAddVehiclesModalClose = (): void => {
    setOpenAddVehiclesModal(false);
    // Optionally refresh group data after vehicles are added
    fetchGroupData();
  };

  const SAFE_TEXT = /^[^+=@\-]*$/;

  const [errors, setErrors] = useState<{ groupDesc?: string }>({});

  return (
    <div className="flex flex-col w-full h-full">
      {/* Header Section */}
      <div className="flex flex-col gap-4 p-4">
        <div className="flex items-center">
          <Button
            startIcon={<img src={BackIcon} alt="back" className="w-6 h-6" />}
            onClick={handleBackClick}
            className="text-[#5547dd] font-bold p-0 hover:bg-transparent"
            sx={{
              fontSize: '16px',
              lineHeight: '24px',
              textTransform: 'none',
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            Back
          </Button>
        </div>
        <Typography variant="h4" sx={{ fontSize: '24px', lineHeight: '36px' }}>
          Edit Depot
        </Typography>
      </div>

      {/* Group Details Section */}
      <div className="flex items-start lg:flex-row md:flex-row flex-col gap-4">
        <div className="flex flex-col lg:w-1/3 md:w-1/2 w-full">
          <div>
            <InputField
              label="Depot Name"
              name="groupName"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value.replace(/\s/g, ''))}
              isDisabled
            />
          </div>
          <div className='w-full flex justify-between items-start mt-2'>
            <div>
              <InputField
                label="Depot Code"
                name="depotCode"
                value={depotCode}
                // onChange={(e) => setGroupName(e.target.value.replace(/\s/g, ""))}
                onChange={() => console.log('depot code change')}
                isDisabled
              />
            </div>
            <div>
              <InputField
                label="Region"
                name="region"
                value={region}
                // onChange={(e) => setGroupName(e.target.value.replace(/\s/g, ""))}
                onChange={() => console.log('depot code change')}
                isDisabled
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:w-2/3 md:w-1/2 w-full">
          <Typography
            variant="body2"
            fontWeight={600}
            color="textPrimary"
            className="self-start flex items-center space-x-1"
          >
            <span>Depot Description</span>
            <span className="text-red-500" title="Required field">
              *
            </span>
          </Typography>

          <textarea
            name="groupDescription"
            value={groupDescription}
            // onChange={(e) => setGroupDescription(e.target.value)}
            onChange={(e) => {
              const inputValue = e.target.value;
              const sanitizedValue = DOMPurify.sanitize(inputValue, {
                ALLOWED_TAGS: [], // remove all HTML tags
                ALLOWED_ATTR: [],
              });

              setGroupDescription(sanitizedValue);

              // live validation
              if (sanitizedValue && !SAFE_TEXT.test(sanitizedValue)) {
                setErrors((p) => ({ ...p, groupDesc: "Depot Description cannot contain + - = @" }));
              } else {
                setErrors((p) => ({ ...p, groupDesc: "" }));
              }
            }}
            rows={4}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '8px',
              border: '1px solid #ccc',
              fontSize: '14px',
              outline: 'none',
              resize: 'vertical',
              marginTop: '8px',
              color: '#242424',
            }}
          />
        </div>
      </div>

      <div className="mt-6"></div>

      {/* Vehicle Table Section */}
      <div className="flex flex-col w-full h-full px-4">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <Typography variant="h6" className="text-[18px] font-semibold">
            Vehicles ({filteredVehicles.length})
          </Typography>
          <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto">
            <div className="relative w-full md:w-[320px]">
              <input
                type="text"
                placeholder="Search a Vehicle"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full h-[45px] px-4 py-3 rounded-lg"
                style={{
                  border: `1px solid ${theme.palette.divider}`,
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.background.paper,
                  paddingLeft: '3rem',
                }}
              />
              <img src={search} alt="search" className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4" />
            </div>
            <Button
              variant="contained"
              className="hover:bg-[#4a3dd1] h-[45px] rounded-lg w-full md:w-auto"
              startIcon={<img src={plus} width={20} height={20} alt="add" />}
              sx={{ textTransform: 'none' }}
              onClick={handleAddVehiclesModalOpen}
            >
              Add Vehicles
            </Button>
          </div>
        </div>

        <TableContainer className="mt-4">
          <Table>
            <TableHead>
              <TableRow className="bg-[#f6f6fd]">
                <TableCell sx={{ fontWeight: 600, fontSize: '14px', lineHeight: '21px' }}>
                  Vehicle Reg. No.
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '14px', lineHeight: '21px' }}>
                  Vehicle Name
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '14px', lineHeight: '21px' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedVehicles.length > 0 ? (
                paginatedVehicles.map((vehicle, index) => {
                  const regNo = typeof vehicle === 'string' ? vehicle : vehicle.regNo;
                  const name = typeof vehicle === 'string' ? '' : vehicle.name;
                  return (
                    <TableRow key={index} className={index % 2 === 0 ? 'bg-[#f6f6fd]' : 'bg-white'}>
                      <TableCell sx={{ fontWeight: 400, fontSize: '14px', lineHeight: '21px' }}>
                        <Checkbox
                          sx={{
                            color: '#5547dd',
                            '&.Mui-checked': { color: '#5547dd' },
                            padding: 0,
                            marginRight: '16px',
                          }}
                        />
                        {regNo}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 400, fontSize: '14px', lineHeight: '21px' }}>
                        {name || 'NA'}
                      </TableCell>
                      <TableCell className="flex items-center space-x-2">
                        <IconButton>
                          <img src={eye} alt="view" className="w-6 h-6" />
                        </IconButton>
                        <IconButton onClick={() => handleDeleteVehicle(index)}>
                          <img src={deleteIcon} alt="delete" className="w-6 h-6" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No vehicles available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack spacing={2} className="mt-4 items-center">
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            shape="rounded"
            sx={{
              '& .MuiPaginationItem-root': { color: '#808080', minWidth: '24px', height: '24px' },
              '& .Mui-selected': {
                backgroundColor: '#eeedfc !important',
                color: '#5547dd !important',
                fontWeight: 'bold',
              },
              '& .MuiPaginationItem-ellipsis': { color: '#808080' },
            }}
          />
        </Stack>
      </div>

      {/* Footer Section */}
      <div className="flex flex-col md:flex-row justify-between items-center w-full gap-4 p-4 border-t border-[#e0e0e0]">
        <Button
          variant="outlined"
          className="h-12 px-5 py-3 rounded-lg border border-[#5547dd] text-[#5547dd] hover:bg-[#5547dd] hover:text-white w-full md:w-auto"
          sx={{ fontWeight: 700, fontSize: '14px', lineHeight: '21px', textTransform: 'none' }}
          onClick={handleBackClick}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          className="h-12 px-5 py-3 rounded-lg bg-[#5547dd] text-white hover:bg-[#4436cc] w-full md:w-auto"
          onClick={handleSaveChanges}
          sx={{ fontWeight: 700, fontSize: '14px', lineHeight: '21px', textTransform: 'none' }}
        >
          Save Changes
        </Button>
      </div>

      {/* Toast Alert */}
      <AlertToast
        message={toastMessage}
        type={toastType}
        open={toastOpen}
        onClose={() => setToastOpen(false)}
      />

      {/* Add Group Vehicles Modal */}
      <Dialog open={openAddVehiclesModal} onClose={handleAddVehiclesModalClose} fullWidth maxWidth="md">
        <AddGroupVehicles
          open={openAddVehiclesModal}
          onClose={handleAddVehiclesModalClose}
          onAddVehicles={() => {
            handleAddVehiclesModalClose();
          }}
          groupData={groupData?.data}
        />
      </Dialog>
    </div>
  );
};

export default EditGroup;
